import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Search } from 'lucide-react';
import Header from '../components/Layout/Header';
import FloatingButton from '../components/Layout/FloatingButton';
import { appointments, doctors } from '../data/mockData';

// Obtener lista de médicos únicos de las citas
const doctorNames = [...new Set(appointments.map(apt => apt.doctor))];

const PageContainer = styled.div`
  flex: 1;
  background: ${({ theme }) => theme.colors.background};
  padding-bottom: 80px;
  overflow-y: auto;
`;

const SearchSection = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-bottom: ${({ $isVisible }) => $isVisible ? '1px' : '0'} solid ${({ theme }) => theme.colors.border};
  overflow: hidden;
  max-height: ${({ $isVisible }) => $isVisible ? '80px' : '0'};
  padding: ${({ $isVisible }) => $isVisible ? '16px 20px' : '0 20px'};
  opacity: ${({ $isVisible }) => $isVisible ? '1' : '0'};
  transition: all 0.3s ease;
`;

const SearchBar = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  svg {
    position: absolute;
    left: 16px;
    width: 20px;
    height: 20px;
    color: ${({ theme }) => theme.colors.textSecondary};
  }

  input {
    width: 100%;
    padding: 13px 16px 13px 48px;
    border: none;
    border-radius: 12px;
    font-size: 15px;
    background: #F5F5F5;
    transition: all 0.3s ease;

    &:focus {
      outline: none;
      background: #EBEBEB;
    }

    &::placeholder {
      color: ${({ theme }) => theme.colors.textSecondary};
    }
  }
`;

const Content = styled.div`
  padding: 0;
`;

const CalendarSection = styled.div`
  background: ${({ theme }) => theme.colors.white};
  padding: 20px 20px 24px;

  @media (min-width: 1024px) {
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
`;

const ContentWrapper = styled.div`
  @media (min-width: 1024px) {
    padding: 20px;
  }
`;

const TopSection = styled.div`
  @media (min-width: 1024px) {
    display: flex;
    gap: 24px;
    margin-bottom: 24px;
  }
`;

const CalendarColumn = styled.div`
  @media (min-width: 1024px) {
    flex-shrink: 0;
    width: 350px;
  }
`;

const FiltersColumn = styled.div`
  @media (min-width: 1024px) {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
    overflow: hidden;
    justify-content: center;
    background: ${({ theme }) => theme.colors.white};
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    padding: 16px;
  }
`;

const BottomSection = styled.div`
  @media (min-width: 1024px) {
    width: 100%;
  }
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const MonthYear = styled.h2`
  font-size: 17px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`;

const NavButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const NavButton = styled.button`
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  transition: all 0.3s ease;

  svg {
    width: 20px;
    height: 20px;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.gray};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const WeekDays = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 8px;
  margin-bottom: 12px;
`;

const WeekDay = styled.div`
  text-align: center;
  font-size: 13px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.textSecondary};
  padding: 8px 0;
`;

const DaysGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  justify-items: center;
  
  @media (min-width: 1024px) {
    gap: 6px;
  }
`;

const DayCell = styled.button`
  width: clamp(32px, 10vw, 44px);
  height: clamp(32px, 10vw, 44px);
  border: none;
  background: ${({ $isToday, $isSelected, theme }) => 
    $isSelected ? theme.colors.primary : 
    $isToday ? theme.colors.info : 
    'transparent'};
  color: ${({ $isToday, $isSelected, $isOtherMonth, theme }) => 
    $isSelected ? theme.colors.white :
    $isOtherMonth ? '#D0D0D0' :
    $isToday ? theme.colors.primary :
    theme.colors.text};
  border-radius: 50%;
  font-size: clamp(13px, 3vw, 15px);
  font-weight: ${({ $isSelected, theme }) => 
    $isSelected ? theme.fontWeights.semibold : theme.fontWeights.normal};
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  @media (min-width: 1024px) {
    width: 38px;
    height: 38px;
  }

  &:hover {
    background: ${({ $isSelected, theme }) => 
      $isSelected ? theme.colors.primaryDark : theme.colors.gray};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.3;
  }
`;

const ViewTabs = styled.div`
  display: flex;
  gap: 8px;
  padding: 16px 20px;
  background: ${({ theme }) => theme.colors.white};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  overflow-x: auto;

  @media (min-width: 1024px) {
    justify-content: flex-start;
    padding: 0 0 16px 0;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }

  &::-webkit-scrollbar {
    height: 4px;
  }
`;

const Tab = styled.button`
  background: ${({ $active, theme }) => 
    $active ? theme.colors.primary : '#F0F0F0'};
  color: ${({ $active, theme }) => 
    $active ? theme.colors.white : theme.colors.text};
  border: none;
  border-radius: 20px;
  padding: 10px 28px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.3s ease;

  &:hover {
    background: ${({ $active, theme }) => 
      $active ? theme.colors.primaryDark : '#E0E0E0'};
  }
`;

const FilterTabsWrapper = styled.div`
  position: relative;
  background: ${({ theme }) => theme.colors.white};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  @media (min-width: 1024px) {
    display: flex;
    align-items: center;
    padding: 12px 0;
    border-radius: 0;
    margin: 0;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};

    &:last-child {
      border-bottom: none;
      padding-bottom: 0;
    }
  }
`;

const FilterLabel = styled.span`
  display: none;
  
  @media (min-width: 1024px) {
    display: block;
    font-size: 13px;
    font-weight: ${({ theme }) => theme.fontWeights.medium};
    color: ${({ theme }) => theme.colors.textSecondary};
    min-width: 80px;
    flex-shrink: 0;
  }
`;

const FilterScrollButton = styled.button`
  display: none;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.border};
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.3s ease;
  z-index: 2;

  svg {
    width: 16px;
    height: 16px;
    color: ${({ theme }) => theme.colors.textSecondary};
  }

  &:hover {
    background: ${({ theme }) => theme.colors.gray};
    border-color: ${({ theme }) => theme.colors.primary};

    svg {
      color: ${({ theme }) => theme.colors.primary};
    }
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  @media (min-width: 1024px) {
    display: flex;
  }
`;

const FilterTabs = styled.div`
  display: flex;
  gap: 8px;
  padding: 16px 20px;
  background: ${({ theme }) => theme.colors.white};
  overflow-x: auto;

  &::-webkit-scrollbar {
    height: 4px;
  }

  @media (min-width: 1024px) {
    flex: 1;
    overflow-x: hidden;
    padding: 0 8px;
    scroll-behavior: smooth;
  }
`;

const FilterTab = styled.button`
  background: ${({ $active, theme }) => 
    $active ? theme.colors.text : 'transparent'};
  color: ${({ $active, theme }) => 
    $active ? theme.colors.white : theme.colors.textSecondary};
  border: none;
  padding: 8px 18px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.3s ease;
  border-radius: 8px;

  &:hover {
    background: ${({ $active, theme }) => 
      $active ? theme.colors.text : '#F0F0F0'};
  }
`;

const AppointmentsList = styled.div`
  padding: 20px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 12px;

  svg {
    width: 48px;
    height: 48px;
    color: ${({ theme }) => theme.colors.textSecondary};
    margin-bottom: 16px;
  }

  p {
    font-size: 15px;
    color: ${({ theme }) => theme.colors.textSecondary};
    margin: 0;
  }
`;

const AppointmentCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 12px;
  padding: 18px;
  margin-bottom: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    transform: translateX(2px);
  }
`;

const AppointmentTime = styled.div`
  font-size: 18px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 10px;
`;

const AppointmentPatient = styled.div`
  font-size: 16px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 4px;
`;

const AppointmentType = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 10px;
`;

const AppointmentFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const DoctorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};

  svg {
    width: 14px;
    height: 14px;
  }
`;

const StatusBadge = styled.span`
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  background: ${({ $status, theme }) => {
    switch ($status) {
      case 'Confirmada':
        return theme.colors.success;
      case 'Pendiente':
        return theme.colors.warning;
      case 'Reprogramada':
        return theme.colors.info;
      case 'Cancelada':
        return theme.colors.danger;
      case 'Completada':
        return '#E8F5E9';
      default:
        return theme.colors.gray;
    }
  }};
  color: ${({ $status, theme }) => {
    switch ($status) {
      case 'Confirmada':
        return theme.colors.successText;
      case 'Pendiente':
        return theme.colors.warningText;
      case 'Reprogramada':
        return theme.colors.infoText;
      case 'Cancelada':
        return theme.colors.dangerText;
      case 'Completada':
        return '#2E7D32';
      default:
        return theme.colors.text;
    }
  }};
`;

const Agenda = () => {
  const navigate = useNavigate();
  const today = new Date();
  const [selectedView, setSelectedView] = useState('Mes');
  const [selectedDoctorFilter, setSelectedDoctorFilter] = useState('');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('');
  const [selectedDate, setSelectedDate] = useState(today.getDate());
  const [currentDate, setCurrentDate] = useState(new Date(today.getFullYear(), today.getMonth(), today.getDate()));
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef(null);
  const doctorFilterRef = useRef(null);
  const statusFilterRef = useRef(null);

  // Cuando cambie a vista semana, actualizar currentDate al día seleccionado
  useEffect(() => {
    if (selectedView === 'Semana') {
      const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), selectedDate);
      setCurrentDate(newDate);
    }
  }, [selectedView]);

  const toggleSearch = () => {
    setShowSearch(prev => !prev);
  };

  const scrollFilters = (ref, direction) => {
    if (ref.current) {
      const scrollAmount = 150;
      ref.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    if (showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showSearch]);

  // Obtener la fecha seleccionada en formato YYYY-MM-DD
  const getSelectedDateString = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const day = selectedDate;
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  // Filtrar citas según los filtros seleccionados y el día seleccionado
  const filteredAppointments = appointments.filter(apt => {
    const matchDate = apt.date === getSelectedDateString();
    const matchDoctor = !selectedDoctorFilter || apt.doctor === selectedDoctorFilter;
    const matchStatus = !selectedStatusFilter || apt.status === selectedStatusFilter;
    const matchSearch = !searchQuery || 
      apt.patient.toLowerCase().includes(searchQuery.toLowerCase()) ||
      apt.type.toLowerCase().includes(searchQuery.toLowerCase()) ||
      apt.doctor.toLowerCase().includes(searchQuery.toLowerCase());
    return matchDate && matchDoctor && matchStatus && matchSearch;
  });

  // Navegar al mes/semana anterior
  const goToPrevious = () => {
    if (selectedView === 'Mes') {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
      setSelectedDate(1);
    } else {
      const newDate = new Date(currentDate);
      newDate.setDate(newDate.getDate() - 7);
      setCurrentDate(newDate);
      setSelectedDate(newDate.getDate());
    }
  };

  // Navegar al mes/semana siguiente
  const goToNext = () => {
    if (selectedView === 'Mes') {
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
      setSelectedDate(1);
    } else {
      const newDate = new Date(currentDate);
      newDate.setDate(newDate.getDate() + 7);
      setCurrentDate(newDate);
      setSelectedDate(newDate.getDate());
    }
  };

  // Obtener nombre del mes
  const getMonthName = (date) => {
    const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    return months[date.getMonth()];
  };

  // Obtener texto del encabezado según la vista
  const getHeaderText = () => {
    if (selectedView === 'Mes') {
      return `${getMonthName(currentDate)} ${currentDate.getFullYear()}`;
    } else {
      // Para vista de semana, mostrar rango de fechas
      const startOfWeek = getStartOfWeek(currentDate);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(endOfWeek.getDate() + 6);
      
      if (startOfWeek.getMonth() === endOfWeek.getMonth()) {
        return `${startOfWeek.getDate()} - ${endOfWeek.getDate()} ${getMonthName(startOfWeek)} ${startOfWeek.getFullYear()}`;
      } else {
        return `${startOfWeek.getDate()} ${getMonthName(startOfWeek)} - ${endOfWeek.getDate()} ${getMonthName(endOfWeek)} ${endOfWeek.getFullYear()}`;
      }
    }
  };

  // Obtener el primer día de la semana (lunes)
  const getStartOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Ajustar cuando el día es domingo
    return new Date(d.setDate(diff));
  };

  const generateCalendarDays = () => {
    const days = [];
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    if (selectedView === 'Semana') {
      // Vista de semana: solo 7 días
      const startOfWeek = getStartOfWeek(currentDate);
      for (let i = 0; i < 7; i++) {
        const dayDate = new Date(startOfWeek);
        dayDate.setDate(startOfWeek.getDate() + i);
        days.push({ 
          day: dayDate.getDate(), 
          isOtherMonth: dayDate.getMonth() !== month,
          fullDate: dayDate
        });
      }
    } else {
      // Vista de mes
      const firstDayOfMonth = new Date(year, month, 1);
      const lastDayOfMonth = new Date(year, month + 1, 0);
      const daysInMonth = lastDayOfMonth.getDate();
      
      // Día de la semana del primer día (0 = domingo, 1 = lunes, etc.)
      let firstDayOfWeek = firstDayOfMonth.getDay();
      // Ajustar para que la semana empiece en lunes
      firstDayOfWeek = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
      
      // Días del mes anterior
      const prevMonth = new Date(year, month, 0);
      const daysInPrevMonth = prevMonth.getDate();
      for (let i = firstDayOfWeek - 1; i >= 0; i--) {
        days.push({ 
          day: daysInPrevMonth - i, 
          isOtherMonth: true,
          fullDate: new Date(year, month - 1, daysInPrevMonth - i)
        });
      }
      
      // Días del mes actual
      for (let i = 1; i <= daysInMonth; i++) {
        days.push({ 
          day: i, 
          isOtherMonth: false,
          fullDate: new Date(year, month, i)
        });
      }
      
      // Días del mes siguiente para completar la grilla
      const remainingDays = 42 - days.length; // 6 semanas x 7 días
      for (let i = 1; i <= remainingDays; i++) {
        days.push({ 
          day: i, 
          isOtherMonth: true,
          fullDate: new Date(year, month + 1, i)
        });
      }
    }

    return days;
  };

  // Verificar si un día es hoy
  const isToday = (dayObj) => {
    const today = new Date();
    return dayObj.fullDate && 
           dayObj.fullDate.getDate() === today.getDate() &&
           dayObj.fullDate.getMonth() === today.getMonth() &&
           dayObj.fullDate.getFullYear() === today.getFullYear();
  };

  // Verificar si un día está seleccionado
  const isSelected = (dayObj) => {
    return dayObj.day === selectedDate && !dayObj.isOtherMonth;
  };

  return (
    <PageContainer>
      <Header title="Agenda"  showSearch onSearchClick={toggleSearch} />
      
      <SearchSection $isVisible={showSearch}>
        <SearchBar>
          <Search />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Buscar citas por paciente, tipo o médico..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </SearchBar>
      </SearchSection>

      <Content>
        <ContentWrapper>
          <TopSection>
            <CalendarColumn>
              <CalendarSection>
                <CalendarHeader>
                  <MonthYear>{getHeaderText()}</MonthYear>
                  <NavButtons>
                    <NavButton onClick={goToPrevious}>
                      <ChevronLeft />
                    </NavButton>
                    <NavButton onClick={goToNext}>
                      <ChevronRight />
                    </NavButton>
                  </NavButtons>
                </CalendarHeader>

                <WeekDays>
                  {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map((day) => (
                    <WeekDay key={day}>{day}</WeekDay>
                  ))}
                </WeekDays>

                <DaysGrid>
                  {generateCalendarDays().map((dayObj, index) => (
                    <DayCell
                      key={index}
                      $isToday={isToday(dayObj)}
                      $isSelected={isSelected(dayObj)}
                      $isOtherMonth={dayObj.isOtherMonth}
                      onClick={() => !dayObj.isOtherMonth && setSelectedDate(dayObj.day)}
                    >
                      {dayObj.day}
                    </DayCell>
                  ))}
                </DaysGrid>
              </CalendarSection>
            </CalendarColumn>

            <FiltersColumn>
              <ViewTabs>
                {['Mes', 'Semana'].map((view) => (
                  <Tab
                    key={view}
                    $active={selectedView === view}
                    onClick={() => setSelectedView(view)}
                  >
                    {view}
                  </Tab>
                ))}
              </ViewTabs>

              <FilterTabsWrapper>
                <FilterLabel>Médico:</FilterLabel>
                <FilterScrollButton onClick={() => scrollFilters(doctorFilterRef, 'left')}>
                  <ChevronLeft />
                </FilterScrollButton>
                <FilterTabs ref={doctorFilterRef}>
                  <FilterTab
                    $active={!selectedDoctorFilter}
                    onClick={() => setSelectedDoctorFilter('')}
                  >
                    Todos los Médicos
                  </FilterTab>
                  {doctorNames.map((doctor) => (
                    <FilterTab
                      key={doctor}
                      $active={selectedDoctorFilter === doctor}
                      onClick={() => setSelectedDoctorFilter(doctor)}
                    >
                      {doctor}
                    </FilterTab>
                  ))}
                </FilterTabs>
                <FilterScrollButton onClick={() => scrollFilters(doctorFilterRef, 'right')}>
                  <ChevronRight />
                </FilterScrollButton>
              </FilterTabsWrapper>

              <FilterTabsWrapper>
                <FilterLabel>Estado:</FilterLabel>
                <FilterScrollButton onClick={() => scrollFilters(statusFilterRef, 'left')}>
                  <ChevronLeft />
                </FilterScrollButton>
                <FilterTabs ref={statusFilterRef}>
                  <FilterTab
                    $active={!selectedStatusFilter}
                    onClick={() => setSelectedStatusFilter('')}
                  >
                    Todos los Estados
                  </FilterTab>
                  <FilterTab
                    $active={selectedStatusFilter === 'Confirmada'}
                    onClick={() => setSelectedStatusFilter('Confirmada')}
                  >
                    Confirmadas
                  </FilterTab>
                  <FilterTab
                    $active={selectedStatusFilter === 'Pendiente'}
                    onClick={() => setSelectedStatusFilter('Pendiente')}
                  >
                    Pendientes
                  </FilterTab>
                  <FilterTab
                    $active={selectedStatusFilter === 'Reprogramada'}
                    onClick={() => setSelectedStatusFilter('Reprogramada')}
                  >
                    Reprogramadas
                  </FilterTab>
                  <FilterTab
                    $active={selectedStatusFilter === 'Cancelada'}
                    onClick={() => setSelectedStatusFilter('Cancelada')}
                  >
                    Canceladas
                  </FilterTab>
                  <FilterTab
                    $active={selectedStatusFilter === 'Completada'}
                    onClick={() => setSelectedStatusFilter('Completada')}
                  >
                    Completadas
                  </FilterTab>
                </FilterTabs>
                <FilterScrollButton onClick={() => scrollFilters(statusFilterRef, 'right')}>
                  <ChevronRight />
                </FilterScrollButton>
              </FilterTabsWrapper>
            </FiltersColumn>
          </TopSection>

          <BottomSection>
            <AppointmentsList>
              {filteredAppointments.length > 0 ? (
                filteredAppointments.map((appointment) => (
                  <AppointmentCard key={appointment.id} onClick={() => navigate(`/detalle-cita/${appointment.id}`)}>
                    <AppointmentTime>{appointment.time}</AppointmentTime>
                    <AppointmentPatient>{appointment.patient}</AppointmentPatient>
                    <AppointmentType>{appointment.type}</AppointmentType>
                    <AppointmentFooter>
                      <DoctorInfo>
                        <CalendarIcon />
                        <span>{appointment.doctor}</span>
                      </DoctorInfo>
                      <StatusBadge $status={appointment.status}>
                        {appointment.status}
                      </StatusBadge>
                    </AppointmentFooter>
                  </AppointmentCard>
                ))
              ) : (
                <EmptyState>
                  <CalendarIcon />
                  <p>No hay citas que coincidan con los filtros</p>
                </EmptyState>
              )}
            </AppointmentsList>
          </BottomSection>
        </ContentWrapper>
      </Content>

      <FloatingButton onClick={() => navigate('/reservar-cita')} />
    </PageContainer>
  );
};

export default Agenda;
